import { Profession } from '@/enums/profession';
import { DbResetType } from '@/shared/stores/db/enums';
import type { ProfessionCooldownQuest, ProfessionCooldownSpell } from '@/types';
import { expansionSubProfession } from '../sub-professions';
import { Expansion } from '@/enums/expansion';

export const expansionProfessionConcentration: Record<number, Record<number, number>> = {
    // Midnight
    11: {
        [Profession.Alchemy]: 3161,
        [Profession.Blacksmithing]: 3162,
        [Profession.Enchanting]: 3163,
        [Profession.Engineering]: 3164,
        [Profession.Inscription]: 3165,
        [Profession.Jewelcrafting]: 3166,
        [Profession.Leatherworking]: 3167,
        [Profession.Tailoring]: 3168,
    },
    // TWW
    10: {
        [Profession.Alchemy]: 3045,
        [Profession.Blacksmithing]: 3040,
        [Profession.Enchanting]: 3046,
        [Profession.Engineering]: 3044,
        [Profession.Inscription]: 3043,
        [Profession.Jewelcrafting]: 3013,
        [Profession.Leatherworking]: 3042,
        [Profession.Tailoring]: 3041,
    },
    // DF
    9: {
        [Profession.Alchemy]: 3054,
        [Profession.Blacksmithing]: 3050,
        [Profession.Enchanting]: 3051,
        [Profession.Engineering]: 3052,
        [Profession.Inscription]: 3053,
        [Profession.Jewelcrafting]: 3047,
        [Profession.Leatherworking]: 3049,
        [Profession.Tailoring]: 3048,
    },
};

// cooldown of 0 = "use the addon data"
export const professionCooldowns: (ProfessionCooldownQuest | ProfessionCooldownSpell)[] = [
    // Alchemy
    {
        type: 'spell',
        key: 'midAlchemyBouquet',
        name: '[Mid] Bouquet of Herbs',
        profession: Profession.Alchemy,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'midAlchemyWondrous',
        name: '[Mid] Wondrous Synergist',
        profession: Profession.Alchemy,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'midAlchemyTransmute',
        name: '[Mid] Transmute',
        profession: Profession.Alchemy,
        cooldown: [
            [64800], // 18h
            // TODO: ???
            // [60480, 2871, 99058, 21], // 16h48m @ 20 points in Transmutation
        ],
    },
    {
        type: 'spell',
        key: 'twwAlchemyMeticulous',
        name: '[TWW] Meticulous Experimentation',
        profession: Profession.Alchemy,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwAlchemyTransmute',
        name: '[TWW] Transmute',
        profession: Profession.Alchemy,
        cooldown: [
            [86400], // 24h
            [60480, 2871, 99058, 21], // 16h48m @ 20 points in Transmutation
        ],
    },
    {
        type: 'spell',
        key: 'dfTransmute',
        name: '[DF] Transmute',
        profession: Profession.Alchemy,
        cooldown: [
            [86400], // 24h
            [60480, 2823, 19538, 21], // 16h48m @ 20 points in Transmutation
        ],
    },
    {
        type: 'spell',
        key: 'wodAlchemySecrets',
        name: '[WoD] Secrets',
        profession: Profession.Alchemy,
        cooldown: [[0]],
    },

    // Blacksmithing
    {
        type: 'spell',
        key: 'twwBlacksmithingEverburning',
        name: '[TWW] Everburning Ignition',
        profession: Profession.Blacksmithing,
        unimportant: true,
        cooldown: [
            [7200], // 2h
            [6300, 2872, 99267, 6], // 25% @ 5 points in Everburning Forge
            [5400, 2872, 99267, 16], // 50% @ 15 points in Everburning Forge
            [4500, 2872, 99267, 26], // 75% @ 25 points in Everburning Forge
            [3600, 2872, 99267, 36], // 100% @ 35 points in Everburning Forge
        ],
    },
    {
        type: 'spell',
        key: 'wodBlacksmithingSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Blacksmithing,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'wodBlacksmithingTruesteel',
        name: '[WoD] Truesteel',
        profession: Profession.Blacksmithing,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopBlacksmithingTrillium',
        name: '[MoP] Balanced Trillium',
        profession: Profession.Blacksmithing,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopBlacksmithingLightning',
        name: '[MoP] Lightning Steel',
        profession: Profession.Blacksmithing,
        cooldown: [[0]],
    },

    // Enchanting
    {
        type: 'spell',
        key: 'wodEnchantingSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Enchanting,
        cooldown: [[0]],
    },

    // Engineering
    {
        type: 'spell',
        key: 'twwBoxOBooms',
        name: "[TWW] Box o' Booms",
        profession: Profession.Engineering,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwInvent',
        name: '[TWW] Invent',
        profession: Profession.Engineering,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'dfSuspiciouslySilent',
        name: '[DF ] Suspiciously Silent',
        profession: Profession.Engineering,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'dfSuspiciouslyTicking',
        name: '[DF ] Suspiciously Ticking',
        profession: Profession.Engineering,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'wodEngineeringSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Engineering,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopEngineeringPeculiar',
        name: "[MoP] Jard's Peculiar",
        profession: Profession.Engineering,
        cooldown: [[0]],
    },

    // Herbalism
    {
        type: 'spell',
        key: 'midHerbalismOverload',
        name: '[Mid] Overload Herb',
        profession: Profession.Herbalism,
        unimportant: true,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwHerbalismOverload',
        name: '[TWW] Overload Herb',
        profession: Profession.Herbalism,
        unimportant: true,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'dfHerbalismOverload',
        name: '[DF] Overload Herb',
        profession: Profession.Herbalism,
        unimportant: true,
        cooldown: [[0]],
    },

    // Inscription
    {
        type: 'spell',
        key: 'wodInscriptionSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Inscription,
        cooldown: [[0]],
    },

    // Jewelcrafting
    {
        type: 'spell',
        key: 'twwAlgariAmberPrism',
        name: '[TWW] Algari Amber Prism',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwAlgariEmeraldPrism',
        name: '[TWW] Algari Emerald Prism',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwAlgariOnyxPrism',
        name: '[TWW] Algari Onyx Prism',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwAlgariRubyPrism',
        name: '[TWW] Algari Ruby Prism',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwAlgariSapphirePrism',
        name: '[TWW] Algari Sapphire Prism',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'dfJeweledDragonsHeart',
        name: "[DF] Jeweled Dragon's Heart",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'dfDreamersVision',
        name: "[DF] Dreamer's Vision",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'dfEarthwardensPrize',
        name: "[DF] Earthwarden's Prize",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'dfKeepersGlory',
        name: "[DF] Keeper's Glory",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'dfQueensGift',
        name: "[DF] Queen's Gift",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'dfTimewatchersPatience',
        name: "[DF] Timewatcher's Patience",
        profession: Profession.Jewelcrafting,
        cooldown: [
            [72000], // 20h
            [36000, 2829, 28607, 11], // 10h @ 10 points in Glassware
        ],
    },
    {
        type: 'spell',
        key: 'wodJewelcraftingSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopJewelcraftingResearch',
        name: '[MoP] Research',
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopJewelcraftingSerpents',
        name: "[MoP] Serpent's Heart",
        profession: Profession.Jewelcrafting,
        cooldown: [[0]],
    },

    // Leatherworking
    {
        type: 'spell',
        key: 'wodLeatherworkingSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Leatherworking,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopLeatherworkingHardened',
        name: '[MoP] Hardened',
        profession: Profession.Leatherworking,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopLeatherworkingMagnificence',
        name: '[MoP] Magnificence',
        profession: Profession.Leatherworking,
        cooldown: [[0]],
    },

    // Mining
    {
        type: 'spell',
        key: 'midMiningOverload',
        name: '[Mid] Overload Deposit',
        profession: Profession.Mining,
        unimportant: true,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'twwMiningOverload',
        name: '[TWW] Overload Deposit',
        profession: Profession.Mining,
        unimportant: true,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'dfMiningOverload',
        name: '[DF] Overload Deposit',
        profession: Profession.Mining,
        unimportant: true,
        cooldown: [[0]],
    },

    // Skinning
    {
        type: 'quest',
        key: 'midProfessionSkinningGloomclaw',
        name: '[Mid] Skin Gloomclaw (Eversong)',
        profession: Profession.Skinning,
        subProfessionId: expansionSubProfession[Expansion.Midnight][Profession.Skinning],
        minimumLevel: 80,
        reset: DbResetType.Daily,
        ids: [88545],
    },
    {
        type: 'quest',
        key: 'midProfessionSkinningSilverscale',
        name: "[Mid] Skin Silverscale (Zul'Aman)",
        profession: Profession.Skinning,
        subProfessionId: expansionSubProfession[Expansion.Midnight][Profession.Skinning],
        minimumLevel: 80,
        reset: DbResetType.Daily,
        ids: [88526],
    },
    {
        type: 'quest',
        key: 'midProfessionSkinningLumenfin',
        name: '[Mid] Skin Lumenfin (Harandar)',
        profession: Profession.Skinning,
        subProfessionId: expansionSubProfession[Expansion.Midnight][Profession.Skinning],
        minimumLevel: 80,
        reset: DbResetType.Daily,
        ids: [88531],
    },
    {
        type: 'quest',
        key: 'midProfessionSkinningUmbrafang',
        name: '[Mid] Skin Umbrafang (Voidstorm)',
        profession: Profession.Skinning,
        subProfessionId: expansionSubProfession[Expansion.Midnight][Profession.Skinning],
        minimumLevel: 80,
        reset: DbResetType.Daily,
        ids: [88532],
    },
    {
        type: 'quest',
        key: 'midProfessionSkinningNetherscythe',
        name: '[Mid] Skin Netherscythe (Voidstorm)',
        profession: Profession.Skinning,
        subProfessionId: expansionSubProfession[Expansion.Midnight][Profession.Skinning],
        minimumLevel: 80,
        reset: DbResetType.Daily,
        ids: [88524],
    },
    {
        type: 'quest',
        key: 'twwProfessionSkinningSlatefang',
        name: '[TWW] Skin Slatefang',
        profession: Profession.Skinning,
        minimumLevel: 72,
        ids: [84259],
    },
    {
        type: 'quest',
        key: 'dfProfessionSkinningMagmaCobra',
        name: '[DF] Skin Magma Cobra',
        profession: Profession.Skinning,
        ids: [74235],
    },
    {
        type: 'quest',
        key: 'dfProfessionSkinningVerdantGladewarden',
        name: '[DF] Skin Verdant Gladewarden',
        profession: Profession.Skinning,
        ids: [78397],
    },

    // Tailoring
    {
        type: 'spell',
        key: 'midArcanoweave',
        name: '[Mid] Arcanoweave',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            // [43200, 2883, 100304, 1], // 12h @ unlocked Dawnweaving
            // [30240, 2883, 100304, 21], // 8h24m @ 20 points in Dawnweaving
        ],
    },
    {
        type: 'spell',
        key: 'midSunfire',
        name: '[Mid] Sunfire',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            // [43200, 2883, 100302, 1], // 12h @ unlocked Duskweaving
            // [30240, 2883, 100302, 21], // 8h24m @ 20 points in Duskweaving
        ],
    },
    {
        type: 'spell',
        key: 'twwDawnweave',
        name: '[TWW] Dawnweave',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            [43200, 2883, 100304, 1], // 12h @ unlocked Dawnweaving
            [30240, 2883, 100304, 21], // 8h24m @ 20 points in Dawnweaving
        ],
    },
    {
        type: 'spell',
        key: 'twwDuskweave',
        name: '[TWW] Duskweave',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            [43200, 2883, 100302, 1], // 12h @ unlocked Duskweaving
            [30240, 2883, 100302, 21], // 8h24m @ 20 points in Duskweaving
        ],
    },
    {
        type: 'spell',
        key: 'dfAzureweave',
        name: '[DF] Azureweave',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            [43200, 2831, 40072, 1], // 12h @ unlocked Azureweaving
            [30240, 2831, 40072, 21], // 8h24m @ 20 points in Azureweaving
        ],
    },
    {
        type: 'spell',
        key: 'dfChronocloth',
        name: '[DF] Chronocloth',
        profession: Profession.Tailoring,
        cooldown: [
            [60480], // 16h48m
            [43200, 2831, 40070, 1], // 12h @ unlocked Timeweaving
            [30240, 2831, 40070, 21], // 8h24m @ 20 points in Timeweaving
        ],
    },
    {
        type: 'spell',
        key: 'wodTailoringingSecrets',
        name: '[WoD] Secrets',
        profession: Profession.Tailoring,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopTailoringCelestial',
        name: '[MoP] Celestial',
        profession: Profession.Tailoring,
        cooldown: [[0]],
    },
    {
        type: 'spell',
        key: 'mopTailoringImperial',
        name: '[MoP] Imperial',
        profession: Profession.Tailoring,
        cooldown: [[0]],
    },
];

export const professionWorkOrders: ProfessionCooldownSpell[] = [
    // Alchemy
    {
        type: 'spell',
        key: 'orders171',
        name: 'Work Orders',
        profession: Profession.Alchemy,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Blacksmithing
    {
        type: 'spell',
        key: 'orders164',
        name: 'Work Orders',
        profession: Profession.Blacksmithing,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Enchanting
    {
        type: 'spell',
        key: 'orders333',
        name: 'Work Orders',
        profession: Profession.Enchanting,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Engineering
    {
        type: 'spell',
        key: 'orders202',
        name: 'Work Orders',
        profession: Profession.Engineering,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Inscription
    {
        type: 'spell',
        key: 'orders773',
        name: 'Work Orders',
        profession: Profession.Inscription,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Jewelcrafting
    {
        type: 'spell',
        key: 'orders755',
        name: 'Work Orders',
        profession: Profession.Jewelcrafting,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Leatherworking
    {
        type: 'spell',
        key: 'orders165',
        name: 'Work Orders',
        profession: Profession.Leatherworking,
        cooldown: [
            [86400], // 24h
        ],
    },

    // Tailoring
    {
        type: 'spell',
        key: 'orders197',
        name: 'Work Orders',
        profession: Profession.Tailoring,
        cooldown: [
            [86400], // 24h
        ],
    },
];
